// import { DeviceEventEmitter } from 'react-native'
import EventEmitter from '../event-emitter'
import {
  getAppListOnDevice,
  upgradeAll,
  DetectXOSUpgrade,
  // getXOSVersion,
  // DetectXOSReOnline,
} from '@remote/api/adapter/xos-mqtt'
import { getDeviceInfo } from '@remote/api/adapter/api-http/device'
import { getErrorMessage } from '@remote/utils/base'
import { logError, logInfo, uuidv4 } from '@remote/utils/logger'
// import { getUserInfoSync } from '../api/user'
import {
  logs,
  XOS_SYSTEM,
  EVENT_NAMES,
  TASK_ERROR_CODE,
  TASK_STATE_CODE,
  getEvtNameFromType,
} from './helper'

interface IDispatchParams {
  appid: string
  targetVersion: number
}

export interface ITaskSchedulerConfig {
  traceId?: string
  deviceId: string
}
const dftConfig: Required<ITaskSchedulerConfig> = {
  traceId: uuidv4(),
  deviceId: '',
}
class TaskScheduler extends EventEmitter {
  private traceId: string
  private timer: any = null
  /** 轮询检测状态(stop: 未启用; shceduling: 全部更新轮询;) */
  protected state: 'stop' | 'scheduling' = 'stop'
  /** 是否开始更新全部 */
  protected isSchedulingStart = false
  // 计划更新等待队列，scheduling中的任务为本次全部更新的内容
  protected scheduling: string[] = []
  // 处理中队列
  protected handling: string[] = []

  protected errors: { [appid: string]: TASK_ERROR_CODE } = {}
  protected taskStatus: { [appid: string]: TASK_STATE_CODE } = {}
  protected _progress: { [taskId: string]: number } = {}
  protected _detectVersion: { [taskId: string]: number | undefined } = {}
  // 任务暂停暂不支持
  // private paused: string[] = []
  protected config: Required<Omit<ITaskSchedulerConfig, 'traceId'>>
  constructor (config: ITaskSchedulerConfig = dftConfig) {
    super(EVENT_NAMES.map(getEvtNameFromType))
    this.traceId = config.traceId || uuidv4()
    this.config = {
      deviceId: config.deviceId,
    }

    this.dispatch = this.dispatch.bind(this)
    this.pause = this.pause.bind(this)
    this.resume = this.resume.bind(this)
    this.getCurrentState = this.getCurrentState.bind(this)
    this.destroy = this.destroy.bind(this)
  }

  // 将一个任务押入等待队列末尾
  private enqueueATask (taskId: string) {
    if (!this.scheduling.includes(taskId)) {
      this.scheduling.push(taskId)
      this.trigger('pending', {
        appid: taskId,
        type: TASK_STATE_CODE.PENDING,
      })
      this.taskStatus[taskId] = TASK_STATE_CODE.PENDING
    }
  }

  protected pollStart (pollType: number) {
    const traceId = this.traceId
    const deviceId = this.config.deviceId
    logs('[轮询_全部更新]:', pollType)
    if (this.state === 'scheduling') {
      this.pollStop()
    }

    this.state = 'scheduling'
    if (this.timer) {
      clearInterval(this.timer)
    }
    logInfo('service_upgrade', {
      traceId,
      category: 'upgrade_polling',
      action: 'poll_start',
    })

    // 进行检测
    // 如果pollType为1，检测xos升级情况
    // 如果pollType为2，检测应用升级情况
    // 如果pollType为3，检测应用升级情况后将pollType调整为1，再次调用pollStart
    if (pollType === 1) {
      const appid = XOS_SYSTEM
      this.scheduling = []
      this.handling = [appid]
      // 更新系统服务前先暂停应用服务状态轮询
      const onDownloading = () => {
        this.triggerProgress(appid, 20, TASK_STATE_CODE.DOWNLOADING)
      }
      const onDownloaded = () => {
        this.triggerProgress(appid, 45, TASK_STATE_CODE.INSTALLING)
      }
      const onRebooting = () => {
        this.triggerProgress(appid, 75, TASK_STATE_CODE.LAUNCHING)
      }
      const onConnecting = () => {
        this.triggerProgress(appid, 90, TASK_STATE_CODE.LAUNCHING)
      }
      onDownloading()
      logs('[轮询_全部更新][XOS更新开始]')
      DetectXOSUpgrade({
        traceId,
        deviceId,
        onDownloaded,
        onRebooting,
        onConnecting,
      }).then(() => {
        logs('[轮询_全部更新][XOS更新成功]')
        this.completeATask(XOS_SYSTEM)
      }).catch((err) => {
        logs('[轮询_全部更新][XOS更新失败]')
        this.announceAErrorTask(appid, err)
      })
    } else {
      // pollType为2|3
      const handling = this.scheduling.filter(appid => appid !== XOS_SYSTEM)
      const waitingCounts = handling.reduce<{ [appid: string]: number }>((counts, appid) => {
        counts[appid] = 0
        this.taskStatus[appid] = TASK_STATE_CODE.ARRIVED
        return counts
      }, {})

      const onTimeout = () => {
        logs('[轮询_全部更新][应用更新超时]超时应用:', handling)
        this.handling = handling.slice().concat(pollType === 3 ? [XOS_SYSTEM] : [])
        this.trigger('timeout', this.handling.slice())
        this.handling.forEach(appid => {
          if (appid === XOS_SYSTEM) {
            logs('[轮询_全部更新][应用更新超时]XOS修改为更新失败')
          }
          const error = new Error(`${appid} update timeout.`)
          this.announceAErrorTask(appid, error, TASK_ERROR_CODE.TIMEOUT_ERROR)
        })
        handling.length = 0
        this.scheduling.length = 0
        this.handling.length = 0
      }

      const makesureTaskHandling = (appid: string) => {
        // 保证任务已经在处理中的状态，然后在后续调用处理成功或处理失败函数
        this.scheduling = this.scheduling.filter(tid => tid !== appid)
        if (!this.handling.includes(appid)) {
          this.handling.push(appid)
        }
      }

      const finishTask = (appid: string) => {
        const taskIndex = handling.findIndex(tid => tid === appid)
        handling.splice(taskIndex, 1)
        if (!handling.length) {
          if (pollType === 3) {
            this.pollStart(1)
          } else {
            this.pollStop()
          }
        }
      }

      const onTaskError = (appid: string) => {
        makesureTaskHandling(appid)
        finishTask(appid)
      }

      const onTaskComplete = (appid: string) => {
        makesureTaskHandling(appid)
        this.completeATask(appid, finishTask)
      }

      const startHandleScheduledTask = (appid: string) => {
        this.scheduling = this.scheduling.filter(tid => tid !== appid)
        if (!this.handling.includes(appid)) {
          this.handling.push(appid)
        }
      }

      // 存在以下情况(pollType为3)
      //      5秒内最后一个应用更新完成，并且XOS已下载完成并离线，此时getAppListOnDevice会连续失败
      //      当设备更新完成重启之后，可以获取到应用已经更新完成，
      //      但会开始检测XOS更新（即等待XOS重启），此时XOS不会再触发重启，从而导致更新无限等待至超时
      //      需要增加判断，当连续失败3次，则检测设备是否在线，若不在线，则认为应用更新完成，且正在重启设备
      let failCount = 0
      // 轮询间隔时长
      const INTERVAL = 5 * 1000
      // 应用升级总超时时长（需要升级的应用数 * 2分钟）
      const MAX_TIME = handling.length * 2 * 60 * 1000
      // 最大轮询次数
      const MAX_COUNT = MAX_TIME / INTERVAL
      let loopCount = 0
      const detect = () => {
        if (loopCount >= MAX_COUNT) {
          // 超时
          onTimeout()
          this.pollStop()
          return
        }
        loopCount += 1
        getAppListOnDevice(traceId).then((result) => {
          const { list: serviceList } = result
          failCount = 0
          const list = serviceList.filter(s => handling.includes(s.app_id))
          logs('[轮询_全部更新][应用状态轮询]:', list)
          logInfo('service_upgrade', {
            traceId,
            category: 'upgrade_polling',
            action: 'poll',
            app_list: list.map((s) => {
              const { app_id, mqtt, status, version } = s
              return {
                app_id,
                mqtt,
                status,
                version,
                task_status: this.taskStatus[app_id],
                targetVersion: this._detectVersion[app_id],
              }
            }),
          })
          list.forEach((service) => {
            const { app_id, mqtt, status, version } = service
            if (!handling.includes(app_id)) return
            const isMqttMust = ['xl_gx', 'xl_disk', 'xl_shell'].includes(app_id)
            // 更新任务需要等待一次status变为非operational，然后才可以等待检测为operational（再设定一个超时未变成operational的错误）
            if (status === 'operational') {
              const targetVersion = this._detectVersion[app_id] || 0
              const state = this.taskStatus[app_id]
              if (state === TASK_STATE_CODE.ARRIVED && version < targetVersion) {
                // 应用状态长时间未发生变化
                if (waitingCounts[app_id] >= MAX_COUNT) {
                  onTaskError(app_id)
                  const error = new Error(`${app_id} update timeout.`)
                  this.announceAErrorTask(app_id, error, TASK_ERROR_CODE.NOT_REPLY)
                  return
                }
                // 尚未执行更新
                waitingCounts[app_id] += 1
                return
              }
              waitingCounts[app_id] = 0
              const isCompleted = isMqttMust ? mqtt === 'online' : true
              if (isCompleted) {
                if (targetVersion) {
                  if (version >= targetVersion) {
                    onTaskComplete(app_id)
                  } else {
                    onTaskError(app_id)
                    const error = new Error(`${app_id} update failed.`)
                    this.announceAErrorTask(app_id, error, TASK_ERROR_CODE.INSTALL_ERROR)
                  }
                } else {
                  onTaskComplete(app_id)
                }
              } else {
                // mqtt状态异常
              }
            } else if (status === 'error') {
              waitingCounts[app_id] = 0
              onTaskError(app_id)
              const error = new Error(`${app_id} error`)
              this.announceAErrorTask(app_id, error)
            } else {
              waitingCounts[app_id] = 0
              startHandleScheduledTask(app_id)
              const last_progress = this._progress[app_id] || 0
              const increase = ~~(Math.random() * 10 + 1)
              const progress = last_progress + increase >= 100 ? 99 : last_progress + increase
              let state = TASK_STATE_CODE.UNKNOW
              switch (status) {
                case 'booting': {
                  state = TASK_STATE_CODE.LAUNCHING
                  break
                }
                case 'downloading': {
                  state = TASK_STATE_CODE.DOWNLOADING
                  break
                }
                case 'installing': {
                  state = TASK_STATE_CODE.INSTALLING
                  break
                }
                case 'updating': {
                  state = TASK_STATE_CODE.UPDATING
                  break
                }
                case 'ready': {
                  state = TASK_STATE_CODE.READY
                  break
                }
                default: {
                  state = TASK_STATE_CODE.OTHER
                  break
                }
              }
              this.triggerProgress(app_id, progress, state)
            }
          })
        }).catch(err => {
          console.error(err)
          failCount += 1
          // 连续失败3次
          if (pollType === 3 && failCount >= 3) {
            const deviceId = this.config.deviceId
            getDeviceInfo(deviceId).then(info => {
              if (info?.status !== 1) {
                this.pollStop()
                const list = handling.filter(appid => appid !== XOS_SYSTEM)
                list.forEach(onTaskComplete)
              }
            })
          }
        })
      }

      // 开始轮询检测应用升级情况
      this.timer = setInterval(() => detect(), INTERVAL)
    }
  }
  private pollStop () {
    if (this.state !== 'scheduling') return
    logInfo('service_upgrade', {
      traceId: this.traceId,
      category: 'upgrade_polling',
      action: 'poll_stop',
    })
    this.state = 'stop'
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  protected triggerProgress (taskId: string, progress: number, type?: TASK_STATE_CODE) {
    this._progress[taskId] = progress || 0
    if (type) this.taskStatus[taskId] = type
    this.trigger('progress', { appid: taskId, progress, type })
  }

  protected completeATask (taskId: string, callback?: (tid: string) => void) {
    if (this.handling.includes(taskId)) {
      this.handling = this.handling.filter(tid => tid !== taskId)
      this.triggerProgress(taskId, 100, TASK_STATE_CODE.COMPLETED)
      this.errors[taskId] = TASK_ERROR_CODE.NO_ERROR
      setTimeout(() => {
        this.trigger('success', { appid: taskId })
      }, 800)
    }
    if (callback) callback(taskId)
    if (!this.handling.length) {
      if (this.scheduling.length) {
        this.startSchedulingMode()
      } else {
        // 所有计划任务执行完成，退出计划更新模式
        this.quitSchedulingMode()
        // 没有异常的服务，则发送finish事件
        const isAllCompleted = Object.keys(this.errors).every(appid => {
          return this.errors[appid] === TASK_ERROR_CODE.NO_ERROR
        })
        if (isAllCompleted) {
          this.trigger('finished')
        }
      }
    }
  }

  private announceAErrorTask (taskId: string, error: Error, type?: TASK_ERROR_CODE) {
    logs('[失败_事件通知]:', taskId, this.handling.join(';'))
    if (this.handling.includes(taskId)) {
      let errorType = type || TASK_ERROR_CODE.OTHER
      if (!type) {
        switch (this.taskStatus[taskId]) {
          case TASK_STATE_CODE.READY:
          case TASK_STATE_CODE.LAUNCHING: {
            errorType = TASK_ERROR_CODE.LAUNCH_ERROR
            break
          }
          case TASK_STATE_CODE.INSTALLING: {
            errorType = TASK_ERROR_CODE.INSTALL_ERROR
            break
          }
          case TASK_STATE_CODE.DOWNLOADING: {
            errorType = TASK_ERROR_CODE.DOWNLOAD_ERROR
            break
          }
          case TASK_STATE_CODE.SENDING:
          case TASK_STATE_CODE.ARRIVED:
          case TASK_STATE_CODE.UPDATING: {
            errorType = TASK_ERROR_CODE.NOT_REPLY
            break
          }
          default: {
            errorType = TASK_ERROR_CODE.OTHER
            break
          }
        }
      }
      this.handling = this.handling.filter(tid => tid !== taskId)
      this.errors[taskId] = errorType
      this.taskStatus[taskId] = TASK_STATE_CODE.UNKNOW
      logs('[失败_事件通知][触发]:', taskId, type, error)
      this.trigger('error', { appid: taskId, error, type: errorType })
    }
    if (!this.handling.length) {
      if (this.scheduling.length) {
        this.startSchedulingMode()
      } else {
        // 所有计划任务执行完成，退出计划更新模式
        this.quitSchedulingMode()
      }
    }
  }

  protected startSchedulingMode () {
    // 交由设备升级所有任务
    if (this.isSchedulingStart) return
    this.isSchedulingStart = true

    const hasXOS = this.scheduling.includes(XOS_SYSTEM)
    const hasXlApp = this.scheduling.some(appid => appid !== XOS_SYSTEM)
    /** 全部更新类型(1: 只有XOS需要升级; 2: 只有应用需要升级(XOS无需升级); 3: 应用和XOS都需升级) */
    const type = (hasXOS ? 1 : 0) + (hasXlApp ? 2 : 0)
    logs('开始更新全部应用:', this.scheduling, type)
    // 开始实施计划更新（由设备升级所有服务）
    this.trigger('scheduled')
    upgradeAll(type, this.traceId).then(() => {
      this.pollStart(type)
    }).catch((err) => {
      console.error(err)
      logError('service_upgrade', {
        traceId: this.traceId,
        category: 'upgrade_scheduler',
        action: 'update_all_to_last_failed',
        error: err,
        errorText: getErrorMessage(err),
      })
      this.quitSchedulingMode()
      // 先确保计划任务被加入处理队列，在宣告失败时才能触发事件通知
      this.handling = this.scheduling.slice()
      this.scheduling.forEach((appid) => {
        this.announceAErrorTask(appid, err, TASK_ERROR_CODE.NOT_REPLY)
      })
      this.scheduling = []
    })
  }

  protected quitSchedulingMode () {
    logs('[计划更新][全部更新结束]')
    this.isSchedulingStart = false
    this.pollStop()
  }

  public pause (service_appid: string) {
    logs('暂不支持暂停任务')
    this.trigger('paused', { appid: service_appid })
  }
  public resume (service_appid: string) {
    logs('暂不支持恢复暂停任务')
    this.trigger('resume', { appid: service_appid })
  }

  /** 获取当前任务状态（用于页面恢复） */
  public getCurrentState () {
    const state = this.state
    const handling = this.handling.slice()
    const scheduling = this.scheduling.slice()
    const progress = { ...this._progress }
    const errors = { ...this.errors }
    const status = { ...this.taskStatus }

    return {
      state,
      handling,
      scheduling,
      progress,
      errors,
      status,
    }
  }

  /** 批量添加任务: 用于全部更新 */
  public dispatch (list: IDispatchParams[], traceId?: string) {
    if (this.isSchedulingStart) return
    if (traceId) this.traceId = traceId
    // 全部更新任务应当优先于其他pending任务，但需要先等待所有handling任务完结
    list.forEach((s) => {
      const { appid, targetVersion } = s
      this._detectVersion[appid] = targetVersion
      if (this.errors[appid]) {
        this.errors[appid] = TASK_ERROR_CODE.NO_ERROR
      }
      this.taskStatus[appid] = TASK_STATE_CODE.UNKNOW
      this.enqueueATask(appid)
    })

    if (!this.handling.length) {
      // 可以直接执行全部更新
      this.startSchedulingMode()
    } else {
      // 等待handling任务队列清空后开始执行全部更新(this.announceAErrorTask或this.completeATask)
    }
  }

  public destroy () {
    this.scheduling = []
    this.handling = []
    this._progress = {}
    this.errors = {}
    this.taskStatus = {}
    EVENT_NAMES.forEach((eventName) => {
      const evtName = getEvtNameFromType(eventName)
      this.removeAllEventListener(evtName)
    })
  }
}

export default TaskScheduler
